// QuestionMetaBoxesAligned.jsx
import React from "react";
import { Box, Typography, Paper } from "@mui/material";

/**
 * Props:
 *  - questionData: {
 *      difficultyLevel?: string,
 *      correctnessScore?: number, // 0.85
 *      correctnessScoreExplanation?: string
 *    }
 *
 * Behavior:
 *  - Side-by-side on sm+: cards match height because parent uses alignItems: "stretch".
 *  - The confidence card content defines the height; the difficulty card stretches to match.
 *  - Stacks on xs; no refs or ResizeObserver used.
 */
export default function QuestionMetaBoxesAligned({ questionData = {} }) {
    const { difficultyLevel, correctnessScore, correctnessScoreExplanation } = questionData ?? {};

    const getDifficultyBg = (level) => {
        if (!level) return "#f5f5f5";
        const l = String(level).toLowerCase();
        if (l === "easy") return "#4caf50";
        if (l === "medium") return "#ff9800";
        if (l === "hard") return "#f44336";
        return "#e0e0e0";
    };

    const getConfidenceBg = (score) => {
        if (score == null || Number.isNaN(score)) return "#f5f5f5";
        if (score >= 0.85) return "#4caf50";
        if (score >= 0.8) return "#ffeb3b";
        return "#f44336";
    };

    const scoreIsValid = typeof correctnessScore === "number" && !Number.isNaN(correctnessScore);

    const difficultyBg = getDifficultyBg(difficultyLevel);
    const confidenceBg = getConfidenceBg(correctnessScore);

    // fixed badge height so they look consistent
    const BADGE_HEIGHT = 28;

    return (
        <Box
            sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" }, // stack on xs, row on sm+
                alignItems: "stretch", // critical: children stretch to the tallest child's height
                width: "100%",
            }}
        >
            {difficultyLevel && (
                <Paper
                    elevation={1}
                    sx={{
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        // On sm+ take ~33% width and stretch vertically to match the row's height
                        flex: { xs: "1 1 auto", sm: "0 0 33%" },
                        boxSizing: "border-box",
                        // DON'T set explicit height here — we rely on alignItems: "stretch" on the parent
                        alignSelf: "stretch", // explicit but redundant; keeps intent clear
                    }}
                >
                    <Typography variant="caption" sx={{ mb: 0.5 }}>
                        Difficulty
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <Box
                            sx={{
                                height: BADGE_HEIGHT,
                                minWidth: 80,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1,
                                borderRadius: 1,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                bgcolor: difficultyBg,
                                color: "#fff",
                                lineHeight: 1,
                            }}
                        >
                            {difficultyLevel}
                        </Box>
                    </Box>
                </Paper>
            )}

            {(scoreIsValid || correctnessScoreExplanation) && (
                <Paper
                    elevation={1}
                    sx={{
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        // On sm+ this takes the remaining width and its content defines the row height
                        flex: { xs: "1 1 auto", sm: "1 1 67%" },
                        boxSizing: "border-box",
                        alignSelf: "stretch",
                    }}
                >
                    <Typography variant="caption" sx={{ mb: 0.5 }}>
                        Confidence
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                        {scoreIsValid && (
                            <Box
                                sx={{
                                    height: BADGE_HEIGHT,
                                    minWidth: 64,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 1,
                                    borderRadius: 1,
                                    fontWeight: 700,
                                    bgcolor: confidenceBg,
                                    color: confidenceBg === "#ffeb3b" ? "#000" : "#fff",
                                    lineHeight: 1,
                                    flexShrink: 0,
                                }}
                            >
                                {Math.round(correctnessScore * 100)}%
                            </Box>
                        )}

                        {correctnessScoreExplanation ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    flex: 1,
                                }}
                            >
                                {correctnessScoreExplanation}
                            </Typography>
                        ) : null}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
